import React from 'react';

import { useBoolean } from 'utils/react';
import CredentialsView from './views/CredentialsView';
import TabsView from './views/TabsView';
import SettingsTabView from './settings/Settings';
import CompanyMappingTabView from './company_mapping/CompanyMapping';
import AuthenticateView from './settings/Authenticate';
import ProductMappingView from './settings/ProductMapping';
import SyncScheduleView from './settings/SyncSchedule';

import { Actions } from '../../consts';
import { RemoveConfirm } from '../common/Dialogs';
import mainLocalization from './localization';

const ACTIONS = serviceProvider => ({
    [Actions.VERIFY]: ({ service }, payload) => {
        return serviceProvider.verify(service, payload);
    },
    [Actions.CREATE]: async ({ service, installed, onUpdate }, payload) => {
        const result = await serviceProvider.update(service, payload);
        if (result) {
            installed.enable();
        }
        onUpdate();
    },
    [Actions.UPDATE]: async ({ service, onUpdate }, payload) => {
        await serviceProvider.update(service, payload);
        onUpdate();
    },
    [Actions.REMOVE]: async ({ confirmRemove }) => {
        confirmRemove.enable();
    },
    [Actions.REMOVE_CONFIRMED]: async context => {
        const { service, confirmRemove, onUpdate } = context;
        await serviceProvider.remove(service);
        confirmRemove.disable();
        onUpdate(true);
    },
    [Actions.CANCEL]: async ({ confirmRemove }) => {
        confirmRemove.disable();
    },
});

const createActionCallback = context => async (name, payload) => {
    const action = ACTIONS(context.serviceProvider)[name];
    if (action) {
        return await action(context, payload);
    }
};

const defaultComponents = {
    PageView,
    CredentialsView,
    TabsView,
    SettingsTabView,
    AuthenticateView,
    ProductMappingView,
    SyncScheduleView,
    CompanyMappingTabView,
};

const extendComponents = (components = {}) => ({
    ...defaultComponents,
    ...components,
});

export default props => {
    const components = extendComponents(props.extra.config.components);
    return <components.PageView components={components} {...props} />;
};

function PageView({ extra, components }) {
    const { service, config, onUpdate, editable } = extra;
    const { serviceProvider, methods } = config;
    const installed = useBoolean(methods.isInstalled(service));
    const confirmRemove = useBoolean(false);
    const onAction = createActionCallback({
        serviceProvider,
        service,
        installed,
        confirmRemove,
        onUpdate,
    });
    const localization = { ...mainLocalization, ...config.localization };
    const context = { service, config, editable, components, localization };

    const onCancel = () => onAction(Actions.CANCEL);
    if (installed.value) {
        const onConfirmRemove = () => onAction(Actions.REMOVE_CONFIRMED);
        return (
            <React.Fragment>
                {confirmRemove.value && (
                    <RemoveConfirm
                        onConfirm={onConfirmRemove}
                        onCancel={onCancel}
                        integrationLabel={config.label}
                    />
                )}
                <components.TabsView context={context} onAction={onAction} />
            </React.Fragment>
        );
    } else {
        const onVerify = values => onAction(Actions.VERIFY, values);
        const onSave = values => onAction(Actions.CREATE, values);
        return (
            <components.CredentialsView
                created
                context={context}
                onSave={onSave}
                onVerify={onVerify}
                onCancel={onCancel}
            />
        );
    }
}
